import type { SelectionRefusal, SubmissionReadDto } from '../../net/rounds-http';

/** The minimal server-reported facts that decide the selection UI. */
export interface SelectionInput {
  submissionOpen: boolean;
  submission: SubmissionReadDto | null;
  memberKind: string | null;
}

export type SelectionAffordance =
  | { visible: false }
  | {
      visible: true;
      enabled: boolean;
      reason: string | null;
      submissionLabel: string | null;
      confirmation: string;
    };

/** Converts a server candidate reference into the only player-facing identifier: its number. */
export function candidateLabel(candidateRef: string | null): string | null {
  if (candidateRef === null) return null;
  const match = /^run:(\d+)$/.exec(candidateRef);
  return match === null ? 'Selected candidate' : `Candidate ${match[1]}`;
}

/**
 * Selection is governed by the tournament's reported submission window, not an inferred deadline
 * or an invented promise about whether a later selection can replace this one.
 */
export function selectionAffordanceFor(input: SelectionInput): SelectionAffordance {
  if (input.memberKind === 'spectator') {
    return { visible: false };
  }
  return {
    visible: true,
    enabled: input.submissionOpen,
    reason: input.submissionOpen ? null : 'Submissions are closed for this round.',
    submissionLabel: candidateLabel(input.submission?.candidateRef ?? null),
    confirmation: 'Select this candidate for the current round while submissions are open?',
  };
}

/** Server refusals stay values; each gets copy that leaves the editor usable. */
export function selectionRefusalMessage(reason: SelectionRefusal): string {
  switch (reason) {
    case 'submission_closed':
      return 'Submissions are closed for this round.';
    case 'tournament_cancelled':
      return 'This tournament has been cancelled.';
    case 'spectator':
      return 'Spectator members cannot submit a candidate.';
    case 'unknown_candidate':
      return 'That candidate is no longer available.';
    case 'unauthorized':
      return 'You are not authorized to submit this candidate.';
    case 'unavailable':
      return 'Selection is unavailable right now. Please try again.';
  }
}
